import { lazy, Suspense } from 'react'
import { createBrowserRouter, type RouteObject } from 'react-router'
import { RouteErrorBoundary } from './errorBoundary.js'
import { RequireChild, RequireParentGate } from './guards.js'
import { NotFoundScreen } from './NotFoundScreen.js'
import { ALL_ROUTES, type RouteSpec } from './routes.js'
import { AppShell } from './shell.js'

const UiDemo = lazy(() =>
  import('../ui/__demo__/UiDemo.js').then((module) => ({ default: module.UiDemo })),
)

/** Dev only: builds a child event log so the screenshot gate can reach the child routes. */
const SeedScreen = lazy(() =>
  import('../pet/dev/SeedScreen.js').then((module) => ({ default: module.SeedScreen })),
)

function guarded(route: RouteSpec) {
  const { element } = route
  if (route.guard === 'child') return <RequireChild>{element}</RequireChild>
  if (route.guard === 'parent') return <RequireParentGate>{element}</RequireParentGate>
  return element
}

const routeObjects: RouteObject[] = ALL_ROUTES.map((route) => ({
  path: route.path,
  element: guarded(route),
}))

if (import.meta.env.DEV) {
  routeObjects.push({
    path: '/__ui',
    element: (
      <Suspense fallback={null}>
        <UiDemo />
      </Suspense>
    ),
  })
  routeObjects.push({
    path: '/__seed',
    element: (
      <Suspense fallback={null}>
        <SeedScreen />
      </Suspense>
    ),
  })
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppShell />,
    errorElement: <RouteErrorBoundary />,
    children: [...routeObjects, { path: '*', element: <NotFoundScreen /> }],
  },
])
